import { Music, Settings2, Shield, Radio } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { MultiSelect } from "@/components/MultiSelect";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["music"]) => void;
}

export function MusicTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.music;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={Music}
      title="Music System"
      description="Configure your high-performance music cluster. Supports YouTube, Spotify, Soundcloud and more."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="grid gap-6">
        {/* Core Settings */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <Settings2 className="h-4 w-4" />
            Core Configuration
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="Bot Prefix"
              hint="Character used to trigger commands (e.g. .play)"
            >
              <Input
                value={cfg.orbit_prefix}
                onChange={(e) => update("orbit_prefix", e.target.value)}
                placeholder="."
                className="max-w-[100px] font-mono"
              />
            </Field>

            <Field
              label="Command Mode"
              hint="How members interact with the music bot."
            >
              <Select
                value={cfg.orbit_command_mode}
                onValueChange={(v) => update("orbit_command_mode", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prefix">Prefix Only (!play)</SelectItem>
                  <SelectItem value="slash">Slash Only (/play)</SelectItem>
                  <SelectItem value="both">Both (Recommended)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Card>

        {/* Access Control */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <Shield className="h-4 w-4" />
            Access & Permissions
          </div>

          <Field
            label="DJ Roles"
            hint="Roles that can bypass voting and manage the queue. (Admins are always DJs)"
          >
            <MultiSelect
              options={roles.map(r => ({ id: r.id, name: r.name, color: r.color }))}
              value={cfg.dj_role_ids}
              onChange={(v) => update("dj_role_ids", v)}
              prefix="@"
              placeholder="+ add DJ role"
            />
          </Field>

          <Field
            label="Allowed Channels"
            hint="Limit music commands to these channels. Leave empty for all channels."
          >
            <MultiSelect
              options={channels.filter(c => c.type === 0).map(c => ({ id: c.id, name: c.name }))}
              value={cfg.allowed_channel_ids}
              onChange={(v) => update("allowed_channel_ids", v)}
              prefix="#"
              placeholder="+ add channel"
            />
          </Field>
        </Card>

        {/* Audio Engine */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-6">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <Radio className="h-4 w-4" />
            Audio Engine Defaults
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Default Volume</div>
                <div className="text-xs text-muted-foreground">Starting volume for new sessions. (1-150%)</div>
              </div>
              <div className="text-sm font-mono font-bold text-primary">{cfg.default_volume}%</div>
            </div>
            <Input
              type="number"
              min={1}
              max={150}
              value={cfg.default_volume}
              onChange={(e) => update("default_volume", parseInt(e.target.value) || 100)}
              className="max-w-[120px]"
            />
          </div>

          <Field
            label="Max Queue Size"
            hint="Maximum number of songs allowed in a single queue."
          >
            <Input
              type="number"
              value={cfg.max_queue}
              onChange={(e) => update("max_queue", parseInt(e.target.value) || 50)}
              className="max-w-[120px]"
            />
          </Field>
        </Card>
      </div>
    </Section>
  );
}
